/**
 * The tables store's reducer and actions (RESERVATIONS_V2_PLAN.md §4.6, decision 54). It has no saga: the flows load
 * through the tables fetch and record the answer here. A reducer never reads a clock, so every `loadedAt` is given
 * by the caller. A load that starts or fails keeps the old data, and a poll of the live states that finds nothing
 * changed keeps the stored record object, so whatever is built from it (the `Map` a selector hands out) stays the same.
 */
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
// The per-function module: Node's ESM loader (the node:test runs) cannot read a named export from lodash's CommonJS build.
import isEqual from "lodash/isEqual";

import { supportLogout, userLogout } from "../../redux-store/slices/LoginSlice/actions";
import type { ApiError } from "../../api";
import type { BranchFloor, TableOccupancy } from "../types";
import type { TableStatus } from "../vocabulary";
import type { FloorEntry, OccupancyEntry, TablesState } from "./types";

/** The store with nothing loaded: what it starts as and what a sign-out returns it to. */
export const EMPTY_TABLES: TablesState = { floors: {}, occupancy: {} };

/** Which branch an action is about. */
interface BranchTarget {
  /** The branch the load is for. */
  branchId: string;
}

/** The payload of `floorLoaded`: the branch, its floor, and when the answer landed. */
interface FloorLoadedPayload extends BranchTarget {
  /** The branch's floor. */
  floor: BranchFloor;
  /** When the answer landed, in epoch milliseconds. */
  loadedAt: number;
}

/** The payload of `occupancyLoaded`: the branch, each table's state, and when the answer landed. */
interface OccupancyLoadedPayload extends BranchTarget {
  /** Each table's state by table id; build it with `occupancyRecordOf`. */
  statusByTableId: Record<string, TableStatus>;
  /** When the answer landed, in epoch milliseconds. */
  loadedAt: number;
}

/** The payload of `floorLoadFailed` and `occupancyLoadFailed`: the branch and the client's error. */
interface LoadFailedPayload extends BranchTarget {
  /** What the client reported. */
  error: ApiError;
}

/** The plain record of each table's state that a load dispatches, made from the `Map` the tables fetch answers. */
export function occupancyRecordOf(occupancy: TableOccupancy): Record<string, TableStatus> {
  const record: Record<string, TableStatus> = {};
  for (const [tableId, status] of occupancy) {
    record[tableId] = status;
  }
  return record;
}

/** Whether two records hold the same tables with the same states. */
function sameTableStates(first: Record<string, TableStatus>, second: Record<string, TableStatus>): boolean {
  const firstIds = Object.keys(first);
  if (firstIds.length !== Object.keys(second).length) {
    return false;
  }
  return firstIds.every((tableId) => second[tableId] === first[tableId]);
}

/** The branch's floor entry in the draft state, created empty when it has never been asked for. */
function floorEntryToChange(state: TablesState, branchId: string): FloorEntry {
  const existing = state.floors[branchId];
  if (existing !== undefined) {
    return existing;
  }
  const created: FloorEntry = { loading: false };
  state.floors[branchId] = created;
  return created;
}

/** The branch's occupancy entry in the draft state, created empty when it has never been asked for. */
function occupancyEntryToChange(state: TablesState, branchId: string): OccupancyEntry {
  const existing = state.occupancy[branchId];
  if (existing !== undefined) {
    return existing;
  }
  const created: OccupancyEntry = { statusByTableId: {}, loading: false };
  state.occupancy[branchId] = created;
  return created;
}

const slice = createSlice({
  name: "tables",
  initialState: EMPTY_TABLES,
  reducers: {
    /** A branch's floor load began; the floor it already holds stays. */
    floorLoadStarted: (state, action: PayloadAction<BranchTarget>) => {
      floorEntryToChange(state, action.payload.branchId).loading = true;
    },
    /** A branch's floor landed; a floor equal to the stored one keeps the stored object, so nothing built from it redraws. */
    floorLoaded: (state, action: PayloadAction<FloorLoadedPayload>) => {
      const entry = floorEntryToChange(state, action.payload.branchId);
      if (entry.floor === undefined || !isEqual(entry.floor, action.payload.floor)) {
        entry.floor = action.payload.floor;
      }
      entry.loadedAt = action.payload.loadedAt;
      entry.loading = false;
      delete entry.error;
    },
    /** A branch's floor load failed; the floor it already holds stays. */
    floorLoadFailed: (state, action: PayloadAction<LoadFailedPayload>) => {
      const entry = floorEntryToChange(state, action.payload.branchId);
      entry.loading = false;
      entry.error = action.payload.error;
    },
    /** A branch's live table states load began; the states it already holds stay. */
    occupancyLoadStarted: (state, action: PayloadAction<BranchTarget>) => {
      occupancyEntryToChange(state, action.payload.branchId).loading = true;
    },
    /**
     * A branch's live table states landed. When they equal the stored ones, the stored record object stays and only
     * the load time and the loading flag change: a poll that found nothing new must not hand out a new `Map` below.
     */
    occupancyLoaded: (state, action: PayloadAction<OccupancyLoadedPayload>) => {
      const entry = occupancyEntryToChange(state, action.payload.branchId);
      if (!sameTableStates(entry.statusByTableId, action.payload.statusByTableId)) {
        entry.statusByTableId = action.payload.statusByTableId;
      }
      entry.loadedAt = action.payload.loadedAt;
      entry.loading = false;
      delete entry.error;
    },
    /** A branch's live table states load failed; the states it already holds stay. */
    occupancyLoadFailed: (state, action: PayloadAction<LoadFailedPayload>) => {
      const entry = occupancyEntryToChange(state, action.payload.branchId);
      entry.loading = false;
      entry.error = action.payload.error;
    },
  },
  extraReducers: (builder) => {
    // Decision 54: the next person on the terminal must not see the last one's floor state, so both sign-outs empty the store.
    builder.addCase(userLogout, () => EMPTY_TABLES).addCase(supportLogout, () => EMPTY_TABLES);
  },
});

export const { floorLoadStarted, floorLoaded, floorLoadFailed, occupancyLoadStarted, occupancyLoaded, occupancyLoadFailed } = slice.actions;

/** The tables store's reducer, registered in both apps' root reducers. */
export const tablesReducer = slice.reducer;
